import React, {
  Context,
  createContext,
  useContext,
  useEffect,
  useState,
} from "react"
import {
  useGuestSignin,
  useSignin,
  useSignout,
  useSignup,
  useSignupLight,
} from "../services/queries"
import StoreContext from "./store-context"
import { AuthenticateRequest } from "../models/email-authenticate-request"
import { SignupLightPayload, SignupPayload } from "../models/sign-up-form"
import moment from "moment"
import { removeCachedOrder } from "../services/cached-order"
import {
  getCachedUser,
  removeCachedUser,
  setCachedUser,
} from "../services/cached-user"

type AccountContextType = {
  accountType: "user" | "guest"
  isLoadingAuth: boolean
  signinGuestAndClearCaches: () => void
  signinUser: (payload: AuthenticateRequest) => void
  signoutAndClearCaches: () => void
  signupAndSignin: (payload: SignupPayload) => void
  signupLightAndSignin: (payload: SignupLightPayload) => Promise<void>
}

type DefaultAccountContextType = {
  accountType?: "user" | "guest"
  isLoadingAuth: boolean
  signinGuestAndClearCaches: () => void
  signinUser: (payload: AuthenticateRequest) => void
  signoutAndClearCaches: () => void
  signupAndSignin: (payload: SignupPayload) => void
  signupLightAndSignin: (payload: SignupLightPayload) => Promise<void>
}

const defaultContext: DefaultAccountContextType = {
  accountType: undefined,
  isLoadingAuth: true,
  signinGuestAndClearCaches: () => {},
  signinUser: () => {},
  signoutAndClearCaches: () => {},
  signupAndSignin: () => {},
  signupLightAndSignin: () => Promise.resolve(),
}

export const AccountContext = createContext(defaultContext)

type Props = {
  children: React.ReactNode
}

export const AccountProvider = ({ children }: Props) => {
  const [accountType, setAccountType] = useState<"user" | "guest">("guest")
  const [isLoadingAuth, setIsLoadingAuth] = useState(true)
  const { clearCart, setDeliveryAddress } = useContext(StoreContext)
  const { mutateAsync: guestSignin } = useGuestSignin()
  const { mutateAsync: signin } = useSignin()
  const { mutateAsync: signout } = useSignout()
  const { mutateAsync: signup } = useSignup()
  const { mutateAsync: signupLight } = useSignupLight()

  const signinGuestAndClearCaches = async () => {
    clearCart()
    removeCachedUser()
    localStorage?.removeItem("hg::cache::cart")
    localStorage?.removeItem("hg::cache::order")

    const { data: accountSession } = await guestSignin()
    setAccountType("guest")
    setCachedUser(accountSession)
  }

  const signinUser = async (payload: AuthenticateRequest) => {
    const { data } = await signin(payload)
    removeCachedOrder() // TODO: existing guest order cannot be transfered to user
    setDeliveryAddress(null) // TODO: Unite cache clearance in its own function. Refactor clearCart function from StoreContext
    setAccountType("user")
    setCachedUser(data)
  }

  const signoutAndClearCaches = async () => {
    await signout()
    window?.analytics?.track("Logout")
    await signinGuestAndClearCaches()
  }

  const signupAndSignin = async (payload: SignupPayload) => {
    await signup(payload)
    window?.analytics?.track("User sign up")

    await signinUser({
      username: payload.email,
      password: payload.password,
    })
  }

  const signupLightAndSignin = async (payload: SignupLightPayload) => {
    await signupLight(payload)
    window?.analytics?.track("User sign up")

    await signinUser({
      username: payload.email,
      password: payload.password,
    })
  }

  useEffect(() => {
    if (!localStorage || typeof localStorage === "undefined") {
      setIsLoadingAuth(false)
      return
    }

    const parsed = getCachedUser()
    if (!parsed) {
      signinGuestAndClearCaches()
      setIsLoadingAuth(false)
      return
    }

    // load account session from local storage to account context state
    if (moment().isAfter(moment(parsed.expires))) {
      signinGuestAndClearCaches()
      setIsLoadingAuth(false)
      return
    }

    if (parsed.is_signed_up) {
      setAccountType("user")
    } else {
      setAccountType("guest")
    }
    setIsLoadingAuth(false)
  }, [setIsLoadingAuth])

  return (
    <AccountContext.Provider
      value={{
        accountType,
        signinGuestAndClearCaches,
        signinUser,
        signoutAndClearCaches,
        signupAndSignin,
        isLoadingAuth,
        signupLightAndSignin,
      }}
    >
      {children}
    </AccountContext.Provider>
  )
}

export const useAccount = () => {
  return useContext<AccountContextType>(
    AccountContext as Context<AccountContextType>
  )
}
